/**
 * Noise filter — keeps irrelevant errors out of the session stream.
 *
 * Failed requests to analytics/tracking domains are almost always ad-blockers
 * or privacy tools, not product bugs. Recording them as errors makes sessions
 * look broken when nothing is wrong.
 */

/** Third-party domains whose request failures are expected under ad-blockers. */
const IGNORED_ERROR_DOMAINS = [
  "google-analytics.com",
  "analytics.google.com",
  "googletagmanager.com",
  "doubleclick.net",
  "googlesyndication.com",
  "googleadservices.com",
  "connect.facebook.net",
  "facebook.net",
  "bat.bing.com",
  "clarity.ms",
  "hotjar.com",
  "fullstory.com",
  "segment.com",
  "segment.io",
  "mixpanel.com",
  "amplitude.com",
  "px.ads.linkedin.com",
  "snap.licdn.com",
  "static.ads-twitter.com",
  "analytics.twitter.com",
];

/** True when a failed request URL belongs to a known analytics/ads provider. */
export function isIgnoredNetworkError(url: string): boolean {
  try {
    const host = new URL(url, window.location.href).hostname.toLowerCase();
    return IGNORED_ERROR_DOMAINS.some((d) => host === d || host.endsWith("." + d));
  } catch {
    return false;
  }
}

/** Console error messages that carry no actionable information. */
const NOISE_CONSOLE_PATTERNS: RegExp[] = [
  /^Script error\.?$/, // opaque cross-origin error, no message
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i,
  /chrome-extension:\/\//,
  /moz-extension:\/\//,
  /safari-extension:\/\//,
];

/** True when a console error message is known noise. */
export function isNoiseConsoleError(message: string): boolean {
  return NOISE_CONSOLE_PATTERNS.some((p) => p.test(message));
}
